import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '$partials/ui';
import {
    ArrowRightIcon,
    ArrowTopRightIcon,
    BrandSignate,
    FacebookIcon,
    InstagramIcon,
    LinkedinIcon,
    PinterestIcon,
    TwitterIcon
} from '$partials/icon';
import {
    formatPageTitle,
    formatPageUri
} from '$utilities/format';
import Util from '$utilities';

/* ========================================================================= */
/* Configuration */
/* ========================================================================= */

const FORM_ID = '8a3db1e9-dec8-42ce-ba00-3bc2d5d9fb8c';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
  * @type {import('$types/components/partials/global.d.ts').Footer}
*/
const Footer = ({
    footerLinks = [],
    contactDetails = [],
    facebook,
    hubspot,
    instagram,
    linkedin,
    page,
    pinterest,
    signate = {},
    twitter,
}) => {
    const [ message, setMessage ] = useState('');
    const {
        formState: { errors },
        handleSubmit,
        register,
        reset,
    } = useForm();

    const onSubmit = async (data) => {
        try {
            const response = await fetch('/api/crm/v1/form', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': Util.cookies.getItem('crumb')
                },
                body: JSON.stringify({
                    formId: FORM_ID,
                    fields: [{
                        objectTypeId: '0-1',
                        name: 'email',
                        value : data?.email
                    }],
                    context:  {
                        hutk: window.sessionStorage.getItem('hubspot_hubspotutk') || undefined,
                        pageUri: formatPageUri(),
                        pageName: formatPageTitle(page)
                    }
                })
            });

            const result = await response.json();

            if (response.status !== 200) {
                if (result && result.message) {
                    setMessage(result.message);
                    return;
                } else {
                    throw new Error(response.status);
                }
            }
            setMessage(result?.message);
            reset();
        } catch (err) {
            console.error(err);
            setMessage('Sorry, something went wrong, please try again later.');
        }
    };

    const resolveLink = (item) => {
        const linkTo = item.link.linkTo;
        let link = '';
        switch (linkTo) {
            case 'email':
                link = 'mailto:' + item.link[linkTo];
                break;
            case 'anchor':
                link = item.link[linkTo];
                break;
            case 'custom_url':
                link = item.link?.customUrl;
                break;
            case 'file':
                link = item.link[linkTo]?.url;
                break;
            case 'page':
                link = item.link[linkTo]?.url;
                break;
            case 'phone':
                link = 'tel:' + item.link[linkTo];
                break;
        }
        return link;
    };

    return (
        <>
            <div className='container mt9'>
                <h2 className='type-style-2 mb6'>Subscribe to Our Newsletter</h2>
                <form
                    data-analytics='newsletter-signup'
                    data-form-id={FORM_ID}
                    data-portal-id={hubspot?.portalId}
                    onSubmit={handleSubmit(onSubmit)}>
                    <div className='col-12 flex flex-wrap sm-flex-no-wrap'>
                        <div className='col-12 sm-col-8 sm-pr1 pb1 sm-pb0'>
                            <div className='flex width-100 items-center'>
                                <input
                                    placeholder='Enter your email'
                                    {...register('email', { required: 'Please enter your email.' })}
                                    className={`type-style-5 width-100 pr6 pt6 pb6 md-py8 type-style-3 pl5 md-pl6${errors?.['email']?.message ? ' border red' : ' border-0'}`}
                                    id='frm-email'/>
                            </div>
                        </div>
                        <div className='col-12 sm-col-4 sm-pl1 pt1 sm-pt0'>
                            <Button
                                as = 'button'
                                className = 'bg-white type-style-5 items-center black'
                                look = 'fill'
                                renderIcon = {<ArrowRightIcon/>}
                                type='submit'>
                                    Subscribe
                            </Button>
                        </div>
                    </div>
                    {errors?.['email']?.message && (
                        <div
                            className='my5 red width-100'
                            dangerouslySetInnerHTML={{__html: errors?.['email']?.message }}/>
                    )}

                    {message && (
                        <div
                            className='my5 width-100'
                            dangerouslySetInnerHTML={{__html: message }} />
                    )}
                </form>
            </div>
            <div className='bg-peach width-100 pb7 mt10 sm-mt11 md-mt13'>
                <footer className='container'>
                    {signate?.url ? (
                        <img
                            className='signate mtn-3 sm-mtn-4 md-mtn-5'
                            src={signate.url} />
                    ) : (
                        <BrandSignate className='mtn-3 sm-mtn-4 md-mtn-5'/>
                    )}
                    <div className='flex flex-wrap mb7 md-mb9 mt7'>
                        {/* Contact Details + Links - Mobile / Tablet */}
                        <div className='md-hide col-12'>
                            {contactDetails?.map((item) => {
                                return (
                                    <div key={item?.id}>
                                        <div className='flex flex-wrap py6'>
                                            <h3 className='type-style-3 col-12 sm-col-6'>
                                                {item.title}
                                            </h3>
                                            <div
                                                className='rich-text footer-navigation type-style-6 col-12 sm-col-6'
                                                dangerouslySetInnerHTML={{ __html: item.body}}/>
                                        </div>
                                        <hr className='black my0'/>
                                    </div>
                                );
                            })}
                            {footerLinks.map((items) => {
                                return (
                                    <div key={items?.id}>
                                        <div className='flex flex-wrap py6'>
                                            <h3 className='type-style-3 col-12 sm-col-6'>
                                                {items.title}
                                            </h3>
                                            <div className='type-style-6 col-12 sm-col-6'>
                                                {items.links.map((item) => {
                                                    const link = resolveLink(item);
                                                    return (
                                                        <a
                                                            className='block link-reset'
                                                            href={link}
                                                            key={item?.id}
                                                            rel='noreferrer'
                                                            target={item.link?.new_window || item.link?.linkTo === 'file' ? '_blank' : ''}>
                                                            {item.label}
                                                            {item.link?.linkTo === 'file' && <ArrowTopRightIcon/>}
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <hr className='black my0'/>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Desktop: footerLinks left, contactDetails right in 2 columns */}
                        <div className='hide md-show col-12'>
                            <div className='flex'>
                                {/* Left: General Links */}
                                <div className='md-col-4'>
                                    {footerLinks.map((items, idx) => {
                                        return (
                                            <div
                                                className={idx ? 'mt7' : ''}
                                                key={items?.id}>
                                                <h3 className='type-style-3 mb5'>
                                                    {items.title}
                                                </h3>
                                                <div className='type-style-6 mt2'>
                                                    {items.links.map((item) => {
                                                        const link = resolveLink(item);
                                                        return (
                                                            <a
                                                                className='block link-reset'
                                                                href={link}
                                                                key={item?.id}
                                                                rel='noreferrer'
                                                                target={item.link?.new_window || item.link?.linkTo === 'file' ? '_blank' : ''}>
                                                                {item.label}
                                                                {item.link?.linkTo === 'file' && <ArrowTopRightIcon/>}
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Right: Contact Details in 2 columns */}
                                <div className='flex flex-wrap md-col-8'>
                                    {contactDetails?.map((item) => {
                                        return (
                                            <div
                                                className='md-col-6 md-mb7 md-border-left md-px6'
                                                key={item?.id}>
                                                <h3 className='type-style-3 mb5'>
                                                    {item.title}
                                                </h3>
                                                <div
                                                    className='rich-text footer-navigation type-style-6 mt2'
                                                    dangerouslySetInnerHTML={{ __html: item.body}}/>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Copyright & Socials */}
                    <div className='flex justify-between md-mb9'>
                        {/* Copyright */}
                        <div>
                            <p>© Russell Simpson</p>
                            <p>Russell Simpson Ltd trading as Russell Simpson, registered in England & Wales No. 01411487</p>
                            <p>Registered office: 151A Sydney Street, London SW3 6NT</p>
                        </div>
                        <div className='hide md-show'>
                            {twitter && (
                                <a
                                    className='link-reset'
                                    href={twitter}
                                    rel='noreferrer'
                                    target='_blank'>
                                    <TwitterIcon className='ml5'/>
                                </a>
                            )}

                            {facebook && (
                                <a
                                    className='link-reset'
                                    href={facebook}
                                    rel='noreferrer'
                                    target='_blank'>
                                    <FacebookIcon className='ml5'/>
                                </a>
                            )}

                            {pinterest && (
                                <a
                                    className='link-reset'
                                    href={pinterest}
                                    rel='noreferrer'
                                    target='_blank'>
                                    <PinterestIcon className='ml5'/>
                                </a>
                            )}

                            {instagram && (
                                <a
                                    className='link-reset'
                                    href={instagram}
                                    rel='noreferrer'
                                    target='_blank'>
                                    <InstagramIcon className='ml5'/>
                                </a>
                            )}

                            {linkedin && (
                                <a
                                    className='link-reset'
                                    href={linkedin}
                                    rel='noreferrer'
                                    target='_blank'>
                                    <LinkedinIcon className='ml5'/>
                                </a>
                            )}

                        </div>
                    </div>

                    {/* External */}
                    <div className='hide md-show mb9'>
                        <img
                            className='mb-2px mr5'
                            src={'/images/naea-arla.png'}
                            style={{
                                width: '102px',
                                height: '34.5px'
                            }}/>
                        <a
                            href='https://www.tpos.co.uk/'
                            rel='noreferrer'
                            target='_blank'>
                            <img
                                src='/images/TPO_TSI_logos.png'
                                style={{
                                    width: '205px',
                                    height: '42.5px'
                                }}/>
                        </a>
                    </div>

                    {/* Credit */}
                    <div className='hide md-show type-style-5 opacity-0-5'>
                        Designed & Developed by Bureau For Visual Affairs
                    </div>
                </footer>
            </div>
        </>

    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default Footer;
